import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { TableModule } from "@openng/optimus-ui/table";

import { GateComponentsService } from "../../../../services/api/gate-components/gate-components.service";

import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { GateComponentComponent } from "../forms/gate-component/gate-component.component";
import { first } from "rxjs";
import { ConfirmationService } from "@openng/optimus-ui/api";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { HasPermissionDirective } from "../../../../shared/directives/has-permission/has-permission.directive";
import { GateSystem } from "../../../../models/api/asset/gateSystem";

import { Column } from "../../../../models/ui/tableColumn";
import { GateComponent } from "../../../../models/api/gateComponents/gateComponent";

@Component({
  standalone: true,
  selector: "app-components-list",
  imports: [TableModule, SvgIconComponent, TranslateModule, HasPermissionDirective],
  templateUrl: "./components-list.component.html",
  styleUrl: "./components-list.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class ComponentsListComponent implements OnInit {
  private config = inject(DynamicDialogConfig);
  private ref: DynamicDialogRef | null = null;
  private translate = inject(TranslateService);
  private gateComponentsService = inject(GateComponentsService);
  private confirmation = inject(ConfirmationService);
  public permissions = inject(PermissionService);
  public columns: Column[] = [
    {
      field: "label",
      header: "gate_administration.components_list.label",
    },
  ];
  public gateComponents: WritableSignal<GateComponent[] | null> = signal(null);
  private gateSystem!: GateSystem;
  private dialogService = inject(DialogService);

  // Initializes the component
  public ngOnInit() {
    if (this.config.data.gateSystem) {
      this.gateSystem = this.config.data.gateSystem;
      if (this.gateSystem.id) {
        this.getGateComponents(this.gateSystem.id);
      }
    }
  }

  /**
   * Gets the gate components for the given gate system id.
   * @param gateSystemId The id of the gate system.
   */
  private getGateComponents(gateSystemId: string) {
    this.gateComponentsService
      .getGateComponents(gateSystemId)
      .pipe(first())
      .subscribe({
        next: (data: GateComponent[] | null) => {
          if (data) {
            this.gateComponents.set(data);
          }
        },
      });
  }

  /**
   * Opens a dialog for creating a new gate component.
   */
  public onCreateClick() {
    this.ref = this.dialogService.open(GateComponentComponent, {
      header: this.translate.instant("gate_administration.components_list.form.add"),
      modal: true,
      width: "48vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { gateSystem: this.gateSystem },
      closable: true,
    });
    this.ref?.onClose.subscribe({
      next: (data: GateComponent) => {
        if (data) {
          this.onDialogClose(data, "create");
        }
      },
    });
  }

  /**
   * Closes the dialog and performs the action depending on the type.
   * @param gateComponent The gate component to be created or edited.
   * @param actionType The type of action to perform (create or edit).
   */
  private onDialogClose(gateComponent: GateComponent, actionType: string) {
    if (actionType === "create") {
      this.gateComponentsService.createGateComponent(gateComponent).subscribe({
        next: () => {
          if (this.gateSystem.id) {
            this.getGateComponents(this.gateSystem?.id);
          }
        },
      });
    } else if (actionType === "edit") {
      this.gateComponentsService.editGateComponent(gateComponent).subscribe({
        next: () => {
          if (this.gateSystem.id) {
            this.getGateComponents(this.gateSystem?.id);
          }
        },
      });
    }
  }

  /**
   * Opens a dialog for editing a gate component.
   * @param component The gate component to be edited.
   */
  public onEditClick(component: GateComponent) {
    this.ref = this.dialogService.open(GateComponentComponent, {
      header: this.translate.instant("gate_administration.components_list.form.edit"),
      modal: true,
      width: "48vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { gateSystem: this.gateSystem, gateComponent: component },
      closable: true,
    });
    this.ref?.onClose.subscribe({
      next: (data: GateComponent | undefined) => {
        if (data) {
          this.onDialogClose(data, "edit");
        }
      },
    });
  }

  /**
   * Deletes a gate component.
   * @param event The event that triggered the deletion.
   * @param component The gate component to be deleted.
   */
  public onDeleteClick(event: Event, component: GateComponent) {
    this.confirmation.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("gate_administration.components_list.delete"),
      key: "global",
      accept: () => {
        if (component.id) {
          this.gateComponentsService.deleteGateComponent(component.id).subscribe({
            next: () => {
              if (this.gateSystem.id) {
                this.getGateComponents(this.gateSystem?.id);
              }
            },
          });
        }
      },
      reject: () => {
        this.confirmation.close();
      },
    });
  }
}
